import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Modal } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  useGetKycV2PersonQuery,
  useGetKycV2PersonSessionsQuery,
  useUpdateKycV2PersonMutation,
  useUpdateKycV2PersonStatusMutation,
  useCreateKycV2SessionMutation,
} from '../../../features/kycv2/kycv2Slice'
import { PersonInfo } from './components/PersonInfo'
import { SessionsTable } from './components/SessionsTable'
import { MetadataViewer } from './components/MetadataViewer'
import { CreateSessionModal } from './components/CreateSessionModal'
import { UpdateStatusModal } from './components/UpdateStatusModal'
import EditFieldModal from '../../kyc/kycPerson/components/EditFieldModal'
import toaster from '../../../toaster'
import styles from './styles.module.scss'

export const KycV2Person = () => {
  const { id } = useParams()
  const { t } = useTranslation(['common', 'messages'])

  const [showEditFieldModal, setShowEditFieldModal] = useState(false)
  const [selectedField, setSelectedField] = useState({ label: '', value: '' })
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false)
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false)
  const [sessionUrl, setSessionUrl] = useState(null)

  const { data: personData } = useGetKycV2PersonQuery({ id })
  const person = personData?.data

  const { data: sessionsData, isLoading: loadingSessions } = useGetKycV2PersonSessionsQuery({ personId: id })
  const sessions = sessionsData?.data || []

  const [updatePerson] = useUpdateKycV2PersonMutation()
  const [updatePersonStatus] = useUpdateKycV2PersonStatusMutation()
  const [createSession, { isLoading: creatingSession }] = useCreateKycV2SessionMutation()

  const handleEditField = (field, value) => {
    setSelectedField({ label: field, value: value || '' })
    setShowEditFieldModal(true)
  }

  const handleUpdateField = async (data) => {
    try {
      await updatePerson({
        id,
        body: data,
      }).unwrap()
      toaster.success(t('messages:updateSuccess'))
      setShowEditFieldModal(false)
    } catch (error) {
      console.error('[UPDATE PERSON V2 ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    }
  }

  const handleUpdateStatus = async (status) => {
    try {
      await updatePersonStatus({
        id,
        body: { status },
      }).unwrap()
      toaster.success(t('messages:updateSuccess'))
      setShowUpdateStatusModal(false)
    } catch (error) {
      console.error('[UPDATE STATUS V2 ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    }
  }

  const handleCreateSession = async (payload) => {
    try {
      const response = await createSession({
        personId: id,
        body: payload,
      }).unwrap()

      const newSessionUrl = response?.data?.sessionUrl
      if (newSessionUrl) {
        setSessionUrl(newSessionUrl)
        toaster.success(t('messages:sessionCreatedSuccess'))
      }
    } catch (error) {
      console.error('[CREATE SESSION ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:sessionCreatedError'))
    }
  }

  const handleCloseCreateSessionModal = () => {
    setShowCreateSessionModal(false)
    setSessionUrl(null)
  }

  return (
    <Box className={`${styles.container} filter`}>
      <PersonInfo
        person={person}
        onEditField={handleEditField}
        onUpdateStatus={() => setShowUpdateStatusModal(true)}
      />

      <SessionsTable
        sessions={sessions}
        onCreateSession={() => setShowCreateSessionModal(true)}
        loading={loadingSessions}
      />

      <MetadataViewer metadata={person?.metadata} />

      <Modal open={showEditFieldModal} onClose={() => setShowEditFieldModal(false)}>
        <div>
          <EditFieldModal
            setModalOpen={setShowEditFieldModal}
            field={selectedField.label}
            value={selectedField.value}
            onSubmit={handleUpdateField}
            validation={selectedField.label === 'email' ? 'email' : null}
          />
        </div>
      </Modal>

      <CreateSessionModal
        open={showCreateSessionModal}
        onClose={handleCloseCreateSessionModal}
        onSubmit={handleCreateSession}
        loading={creatingSession}
        sessionUrl={sessionUrl}
      />

      <UpdateStatusModal
        open={showUpdateStatusModal}
        onClose={() => setShowUpdateStatusModal(false)}
        onSubmit={handleUpdateStatus}
        currentStatus={person?.status}
      />
    </Box>
  )
}
